import React, { useState } from 'react';
import TopBar from './TopBar';
import './ContactPage.css';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { createBugReport, uploadBugAttachment } from '../services/bugService';

// Contact Page Component
export default function ContactPage() {
  const email = 'CondoEats050126@gmail.com';
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  // Bug Report State
  const [bugData, setBugData] = useState({
    title: '',
    description: '',
    category: '',
    severity: 'low',
    deviceInfo: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailAction = async () => {
    // Try to copy to clipboard
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(email);
        showSuccess('Email address copied to clipboard');
      }
    } catch (err) {
      // Silently fail - not critical since mailto will still work
      console.error('Failed to copy:', err);
    }

    // Always open mail client (regardless of copy success/failure)
    window.location.href = `mailto:${email}`;
  };

  const handleBugChange = (e) => {
    const { name, value } = e.target;
    setBugData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0]);
    }
  };

  const handleBugSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      showError('You must be logged in to report a bug.');
      return;
    }

    // Explicit validation
    if (!bugData.title.trim() || !bugData.category || !bugData.description.trim()) {
      showError('Please fill in all mandatory fields (Title, Category, Description).');
      return;
    }

    try {
      setIsSubmitting(true);

      let attachmentId = null;
      if (attachment) {
        attachmentId = await uploadBugAttachment(attachment);
      }

      await createBugReport({
        ...bugData,
        severity: 'low', // Default severity since user doesn't select it
        attachmentId: attachmentId
      }, user.$id);

      showSuccess('Bug report submitted successfully. Thank you for your feedback!');

      // Reset form
      setBugData({
        title: '',
        description: '',
        category: '',
        deviceInfo: ''
      });
      setAttachment(null);
      // Reset file input visually if needed, but controlled component logic usually preferred or key reset
    } catch (error) {
      console.error('Bug submission error:', error);
      showError(error, 'Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <TopBar />
      <main className="contact-main">
        <div className="contact-container">
          <h1 className="contact-title">Contact Us</h1>

          <div className="contact-content">
            <p className="contact-intro">
              If you have questions, feedback, or issues related to CondoEats, you may contact us at:
            </p>

            <div className="contact-info-box">
              <div className="contact-info-item">
                <span className="contact-label">Email:</span>
                <button
                  type="button"
                  className="contact-email"
                  onClick={handleEmailAction}
                  aria-label="Email Condo Eats"
                  title="Opens your mail client and copies the email address"
                >
                  {email}
                </button>
              </div>
            </div>

            <p className="contact-disclaimer">
              We will make reasonable efforts to respond, but response times are not guaranteed.
            </p>
          </div>

          <div className="bug-report-section">
            <h2 className="section-title">Report a Bug</h2>
            <p className="section-subtitle">Found an issue? Let us know so we can fix it.</p>

            <form className="bug-report-form" onSubmit={handleBugSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title <span className="required-star">*</span></label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={bugData.title}
                  onChange={handleBugChange}
                  placeholder="Brief summary of the issue"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category <span className="required-star">*</span></label>
                <select
                  id="category"
                  name="category"
                  value={bugData.category}
                  onChange={handleBugChange}
                  required
                >
                  <option value="" disabled>Select a category</option>
                  <option value="UI">UI</option>
                  <option value="payment">Payment</option>
                  <option value="authentication">Authentication</option>
                  <option value="ordering">Ordering</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description <span className="required-star">*</span></label>
                <textarea
                  id="description"
                  name="description"
                  value={bugData.description}
                  onChange={handleBugChange}
                  placeholder="Detailed steps to reproduce the bug..."
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="deviceInfo">Device Info (Optional)</label>
                <input
                  type="text"
                  id="deviceInfo"
                  name="deviceInfo"
                  value={bugData.deviceInfo}
                  onChange={handleBugChange}
                  placeholder="e.g. Browser, Device model"
                />
              </div>

              <div className="form-group">
                <label htmlFor="attachment">Attachment (Optional)</label>
                <input
                  type="file"
                  id="attachment"
                  name="attachment"
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </div>

              <button
                type="submit"
                className="submit-bug-btn"
                disabled={isSubmitting || !user}
                title={!user ? "Please login to report a bug" : ""}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
              {!user && <p className="login-hint">Please log in to report a bug.</p>}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}